import React, { useEffect, useState } from 'react';
import InputTextComp from "@components/UI/InputText/Index.jsx";
import ButtonComp from "@components/UI/Button/Index.jsx";
import SelectOptionComp from '@components/UI/SelectOption/Index.jsx';
import InputDatePickerComp from '@components/UI/InputDatePicker/Index.jsx';
import { useFetchDataUnidades } from '@hooks/api/Unidade/useFetchDataUnidades.js';
import { useFetchDataProcessos } from '@hooks/api/Processo/useFetchDataProcessos.js';
import { tipoAssociacaoOptions } from "@enums/RelatorioFormularios/Index.jsx";
import dayjs from 'dayjs';
import { Alert } from 'antd';
import { useFetchDataFormulariosModelos } from '@hooks/api/Formulario/useFetchDataFormulariosModelos';

// Valores iniciais alinhados com os filtros disponíveis
const initialValues = {
  tipoAssociacao: "",
  numeroSolicitacao: "",
  tituloFormulario: "",
  dataEnvioRespostaInicial: "",
  dataEnvioRespostaFinal: "",
  nomeUnidadeGerencia: "",
  nomeProcesso: "",
};

const FilterForms = ({ setFilters }) => {
  const [tempValues, setTempValues] = useState(initialValues);

  // Hooks customizados para selects
  const { unitOptions, unitLoading, fetchUnits } = useFetchDataUnidades();
  const { processosOptions, processosLoading, fetchProcessos } = useFetchDataProcessos();
  const { formulariosModelosOptions, formulariosLoading, fetchFormularios } = useFetchDataFormulariosModelos();

  // Carrega opções ao montar
  useEffect(() => {
    fetchUnits();
    fetchProcessos();
    fetchFormularios();
  }, []);

  // Atualização de campos
  const handleTempChange = (field, value) => {
    setTempValues(prev => ({ ...prev, [field]: value }));
  };

  // Máscara para o Nº da Solicitação
  const handleSolicitacaoInput = e => {
    const value = e.target.value.replace(/-/g, '').toUpperCase();
    const formatted = [
      value.slice(0, 3),
      value.slice(3, 6),
      value.slice(6, 10),
      value.slice(10, 13),
    ].filter(Boolean).join('-');
    handleTempChange("numeroSolicitacao", formatted);
  };

  // Aplicar filtros (envia valores prontos para API)
  const handleSearch = (e) => {
    e.preventDefault();

    setFilters({
      tipoAssociacao: tempValues.tipoAssociacao?.value || "",
      numeroSolicitacao: tempValues.numeroSolicitacao,
      tituloFormulario: tempValues.tituloFormulario?.label || "",
      dataEnvioRespostaInicial: tempValues.dataEnvioRespostaInicial,
      dataEnvioRespostaFinal: tempValues.dataEnvioRespostaFinal,
      nomeUnidadeGerencia: tempValues.nomeUnidadeGerencia?.label || "",
      nomeProcesso: tempValues.nomeProcesso?.label || "",
    });
  };

  // Limpa filtros
  const handleClearFilter = () => {
    setTempValues(initialValues);
    setFilters(initialValues);
  };

  return (
    <form onSubmit={handleSearch} className="form">
      <div className="row col-md-12">
        <div className="col-md-8">

          <div className='mb-3 me-2'>
            <Alert
              type="info"
              showIcon
              message="Preencha um filtro para refinar a busca ou deixe tudo em branco para ver todos os dados."
              className="mt-3"
            />
          </div>

          <InputTextComp
            label="Nº da Solicitação"
            hoverMessage="Número da Solicitação"
            value={tempValues.numeroSolicitacao}
            onChange={handleSolicitacaoInput}
            placeholder="Preencher com o número da solicitação."
            maxLength={16}
          />

          <SelectOptionComp
            label="Modelo do Formulário"
            options={formulariosModelosOptions}
            value={tempValues.tituloFormulario}
            hoverMessage="Título do modelo de formulário associado"
            onChange={form => handleTempChange("tituloFormulario", form)}
            placeholder="Selecionar o modelo do formulário."
            isClearable
          />

          <div className='d-flex'>
            <div className='col-md-6'>
              <InputDatePickerComp
                label="Data de Envio Inicial"
                value={tempValues.dataEnvioRespostaInicial ? dayjs(tempValues.dataEnvioRespostaInicial) : null}
                onChange={selected => handleTempChange(
                  "dataEnvioRespostaInicial",
                  selected ? selected.format("YYYY-MM-DD") : ""
                )}
                placeholder="Selecionar"
                hoverMessage="Selecione a data inicial para as respostas dos formulários enviados"
                format="DD/MM/YYYY"
              />
            </div>
            <div className='col-md-6'>
              <InputDatePickerComp
                label="Data de Envio Final"
                value={tempValues.dataEnvioRespostaFinal ? dayjs(tempValues.dataEnvioRespostaFinal) : null}
                onChange={selected => handleTempChange(
                  "dataEnvioRespostaFinal",
                  selected ? selected.format("YYYY-MM-DD") : ""
                )}
                placeholder="Selecionar"
                hoverMessage="Selecione a data final para as respostas dos formulários enviados"
                format="DD/MM/YYYY"
              />
            </div>
          </div>

          <SelectOptionComp
            label="Unidade/Gerência"
            options={unitOptions}
            value={tempValues.nomeUnidadeGerencia}
            onChange={val => handleTempChange("nomeUnidadeGerencia", val)}
            placeholder="Selecionar uma unidade/gerência"
            hoverMessage="Nome da Unidade/Gerência"
            isLoading={unitLoading}
            isClearable
          />
          <SelectOptionComp
            label="Processo"
            options={processosOptions}
            value={tempValues.nomeProcesso}
            onChange={val => handleTempChange("nomeProcesso", val)}
            placeholder="Selecionar um processo"
            hoverMessage="Nome do processo"
            isLoading={processosLoading}
            isClearable
          />
          <SelectOptionComp
            label="Tipo de Associação"
            hoverMessage="Tipo de Associação do Formulário"
            options={tipoAssociacaoOptions}
            value={tempValues.tipoAssociacao}
            onChange={val => handleTempChange("tipoAssociacao", val)}
            placeholder="Selecionar um tipo de associação"
            isClearable
          />

        </div>

        {/* Botões */}
        <div className="col-8 col-md-8 d-flex flex-wrap justify-content-end mt-4">
          <ButtonComp
            type="submit"
            title="Buscar dados"
            buttonColor="#2b97be"
            classnameIcon="fa-solid fa-filter"
            hoverMessage="Botão para aplicar o filtro da consulta"
          />
          <ButtonComp
            type="button"
            onClick={handleClearFilter}
            title="Limpar filtro"
            buttonColor="#125578"
            classnameIcon="fa-regular fa-circle-xmark"
            hoverMessage="Botão para limpar os campos de filtros preenchidos"
          />
        </div>
      </div>
    </form>
  );
};

export default FilterForms;
